/**
 * Sale events with an evergreen hub at `/{cc}/{lang}/sale/{slug}` (plan W0.15,
 * master D-5 "always indexed"). The URL never carries a year; the year lives
 * in the title and content and follows the dates below.
 *
 * Pure module (no `window`, no React). One config drives the routes, titles,
 * static heads (`staticHeads.json`) and the live-promotions query.
 *
 * Dates are LOCAL calendar days (`YYYY-MM-DD`, inclusive) in the locale's
 * time zone. Islamic dates depend on moon sighting and are marked `expected`.
 *
 * Tag ids differ per environment and come from `VITE_SALE_TAG_IDS`, a JSON
 * object keyed by slug, e.g. `{"11-11":"<ObjectId>","blessed-friday":"<ObjectId>"}`
 * (owner item #1 in apps/web-app/plan.md). A missing or malformed value leaves
 * `tagId` null, and the page shows no live-promotions list.
 */
import { DEFAULT_LOCALE, type Locale } from './locales';
import { parseLocalDay, zonedTimeToDate } from './text';
import { localePath } from './urls';

/** One brand from last year's edition of an event (owner-compiled). */
export interface SaleParticipant {
  /** The brand's name as it should be shown. */
  brandName: string;
  /**
   * The brand's id. With an id, the row is shown only while the brand is
   * publicly visible (and then links to its page), so a blocked brand is
   * never named; without one, the name is shown as plain text.
   */
  brandId?: string;
  /** Last year's sale window, `YYYY-MM-DD`. */
  startDate?: string;
  endDate?: string;
  /** Largest advertised discount, in percent. */
  maxDiscount?: number;
}

export interface SaleEvent {
  /** URL segment: `/pk/en/sale/{slug}`. */
  slug: string;
  /** Display name used in titles: "{name} Sale {YYYY} in {Country}". */
  name: string;
  /** Other names people search for ("White Friday"); shown next to the H1. */
  aliases: readonly string[];
  /** First and last day of this year's edition, `YYYY-MM-DD`, inclusive. */
  startDate: string;
  endDate: string;
  /** The dates are an expectation (moon sighting), not an announcement. */
  expected: boolean;
  /**
   * The hub page is live. Static heads are generated only for published
   * events, so a prerendered head never exists for a page the SPA 404s.
   */
  published: boolean;
  /**
   * Counts as published only in builds with `VITE_PREVIEW_SALE_EVENTS=true`
   * (dev-only; never set in production), so the owner can view the page early.
   */
  preview?: boolean;
  /** OWNER CONTENT: a short original explainer. Empty renders nothing. */
  explainer: string;
  /** OWNER CONTENT: brands that ran a sale in last year's edition. Empty renders nothing. */
  lastYearParticipants: readonly SaleParticipant[];
  /**
   * OWNER CONTENT (artwork): the hub's share image (og:image / twitter:image,
   * in the SPA and in its prerendered head, plan W0.18) — the path of a real
   * 1200×630 JPEG of at most 150 KB under `public/og/`, e.g. `/og/11-11.jpg`.
   * null uses the site image (`/og-image.jpg`). src/seo/staticHeads.test.ts
   * checks the file, and the build fails if it is missing.
   */
  ogImage: string | null;
  /** The Dashboard tag whose promotions the hub lists; null until configured. */
  tagId: string | null;
}

type SaleEventConfig = Omit<SaleEvent, 'tagId'>;

/**
 * Events in date order. Publication follows plan W0.15: 11-11,
 * blessed-friday and 12-12 now; ramadan and eid-ul-fitr by mid-January.
 * Publishing an event also means: regenerate `staticHeads.json`, add its URL
 * to `public/sitemap.xml` (until deploy E7) and publish the event in the
 * backend (Dashboard D14 / `updateSaleEvent(id, input: { published: true })`);
 * its `listFrom` decides when the backend's static.xml lists it (backend B3.6).
 */
const SALE_EVENT_CONFIG: readonly SaleEventConfig[] = [
  {
    slug: '11-11',
    name: '11.11',
    aliases: [],
    startDate: '2026-11-11', // Wed
    endDate: '2026-11-11',
    expected: false,
    published: true,
    // OWNER CONTENT: share artwork (see ogImage); null = the site image.
    ogImage: null,
    // OWNER CONTENT: explainer and last year's participants (plan owner item #1).
    explainer: '',
    lastYearParticipants: [],
  },
  {
    slug: 'blessed-friday',
    name: 'Blessed Friday',
    aliases: ['White Friday', 'Black Friday'],
    startDate: '2026-11-27', // Fri
    endDate: '2026-11-27',
    expected: false,
    published: true,
    // OWNER CONTENT: share artwork (see ogImage); null = the site image.
    ogImage: null,
    // OWNER CONTENT: explainer and last year's participants (plan owner item #1).
    explainer: '',
    lastYearParticipants: [],
  },
  {
    slug: '12-12',
    name: '12.12',
    aliases: [],
    startDate: '2026-12-12', // Sat
    endDate: '2026-12-12',
    expected: false,
    published: true,
    // OWNER CONTENT: share artwork (see ogImage); null = the site image.
    ogImage: null,
    // OWNER CONTENT: explainer and last year's participants (plan owner item #1).
    explainer: '',
    lastYearParticipants: [],
  },
  {
    slug: 'ramadan',
    name: 'Ramadan',
    aliases: [],
    startDate: '2027-02-08',
    endDate: '2027-03-09',
    expected: true,
    published: false,
    preview: true,
    // OWNER CONTENT: share artwork (see ogImage); null = the site image.
    ogImage: null,
    // OWNER CONTENT: explainer and last year's participants (plan owner item #1).
    explainer: '',
    lastYearParticipants: [],
  },
  {
    slug: 'eid-ul-fitr',
    name: 'Eid-ul-Fitr',
    aliases: ['Eid ul Fitr'],
    startDate: '2027-03-10',
    endDate: '2027-03-10',
    expected: true,
    published: false,
    preview: true,
    // OWNER CONTENT: share artwork (see ogImage); null = the site image.
    ogImage: null,
    // OWNER CONTENT: explainer and last year's participants (plan owner item #1).
    explainer: '',
    lastYearParticipants: [],
  },
];

const OBJECT_ID = /^[a-f\d]{24}$/i;

/**
 * `VITE_SALE_TAG_IDS` → `{ slug: tagId }`. Anything that isn't a JSON object
 * of 24-hex ids is ignored entry by entry, so one typo never breaks the page.
 */
export function parseSaleTagIds(raw: unknown): Record<string, string> {
  if (typeof raw !== 'string' || !raw.trim()) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
  const ids: Record<string, string> = {};
  for (const [slug, id] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof id === 'string' && OBJECT_ID.test(id.trim())) ids[slug] = id.trim();
  }
  return ids;
}

// Pure annotations let the bundler drop the config from chunks that only need
// the helpers (`<PageHead/>` imports the templates on every page).
const TAG_IDS = /* @__PURE__ */ parseSaleTagIds(import.meta.env.VITE_SALE_TAG_IDS);

// Build-time, dev-only: blank/false = today; `true` publishes the `preview` events.
const PREVIEW_ENABLED = import.meta.env.VITE_PREVIEW_SALE_EVENTS === 'true';

/** Every configured event (published or not), in date order. */
export const SALE_EVENTS: readonly SaleEvent[] = /* @__PURE__ */ Object.freeze(
  /* @__PURE__ */ SALE_EVENT_CONFIG.map((event) =>
    Object.freeze({
      ...event,
      published: event.published || (event.preview === true && PREVIEW_ENABLED),
      tagId: TAG_IDS[event.slug] ?? null,
    }),
  ),
);

/** Events whose hub page is live, in date order. */
export function publishedSaleEvents(): SaleEvent[] {
  return SALE_EVENTS.filter((event) => event.published);
}

/** The published event for a URL segment, or null (the page is then a 404). */
export function saleEventBySlug(slug: string | null | undefined): SaleEvent | null {
  if (!slug) return null;
  return SALE_EVENTS.find((event) => event.slug === slug && event.published) ?? null;
}

/** The year shown in the event's title and H1 (the year of its first day). */
export function saleEventYear(event: Pick<SaleEvent, 'startDate'>): number {
  const day = parseLocalDay(event.startDate);
  if (!day) throw new RangeError(`saleEventYear: invalid startDate "${event.startDate}"`);
  return day.year;
}

/**
 * The event as instants: from 00:00 of its first day to the last millisecond
 * of its last day, in the locale's time zone (for countdowns and "live now").
 */
export function saleEventWindow(
  event: Pick<SaleEvent, 'startDate' | 'endDate'>,
  locale: Locale = DEFAULT_LOCALE,
): { start: Date; end: Date } {
  const first = parseLocalDay(event.startDate);
  const last = parseLocalDay(event.endDate);
  if (!first || !last) {
    throw new RangeError(`saleEventWindow: invalid dates "${event.startDate}" – "${event.endDate}"`);
  }
  return {
    start: zonedTimeToDate(first, locale.timeZone),
    end: zonedTimeToDate({ ...last, hour: 23, minute: 59, second: 59, ms: 999 }, locale.timeZone),
  };
}

/** Where an event stands at `now`: before its first day, during it, or after its last. */
export type SaleEventPhase = 'upcoming' | 'live' | 'ended';

export function saleEventPhase(
  event: Pick<SaleEvent, 'startDate' | 'endDate'>,
  now: Date = new Date(),
  locale: Locale = DEFAULT_LOCALE,
): SaleEventPhase {
  const { start, end } = saleEventWindow(event, locale);
  const t = now.getTime();
  if (t < start.getTime()) return 'upcoming';
  return t <= end.getTime() ? 'live' : 'ended';
}

/** Published events that are live or upcoming at `now`, in date order. */
export function nextSaleEvents(now: Date = new Date(), locale: Locale = DEFAULT_LOCALE): SaleEvent[] {
  return publishedSaleEvents().filter((event) => saleEventPhase(event, now, locale) !== 'ended');
}

/** The event hub's path: `/pk/en/sale/{slug}` (born at its final URL; never un-prefixed). */
export function saleEventPath(event: Pick<SaleEvent, 'slug'>, locale: Locale = DEFAULT_LOCALE): string {
  return localePath(locale, `/sale/${event.slug}`);
}

/** The sale calendar's path: `/pk/en/sales`. */
export function saleCalendarPath(locale: Locale = DEFAULT_LOCALE): string {
  return localePath(locale, '/sales');
}

/**
 * The `publishedPromotions` filter for an event's tagged offers, as the JSON
 * string `Options.where` takes: `{"tags":{"$in":["<tagId>"]}}`. Null without a
 * valid tag id, so a caller can never fall back to an unfiltered feed (which
 * would list every promotion on the site under the event's name).
 */
export function saleEventPromotionsWhere(tagId: string | null | undefined): string | null {
  const id = tagId?.trim();
  if (!id || !OBJECT_ID.test(id)) return null;
  return JSON.stringify({ tags: { $in: [id] } });
}

// ── Backend SalePage mapping (web plan W3.4) ─────────────────────────────────

/** One edition of an event as the backend returns it (B3.6 / B3.7a). */
export interface BackendSaleWindow {
  year: number;
  /** First day, "YYYY-MM-DD". */
  start: string;
  /** Last day, "YYYY-MM-DD", inclusive. */
  end: string;
  /** Dates are an expectation (moon sighting), not an announcement. */
  expected: boolean;
}

/** A previous-edition participant as the backend returns it from `salePage`. */
export interface BackendSaleParticipant {
  year: number;
  brandName: string;
  /** Set only when the brand is publicly visible; null = plain-text row. */
  brand?: { id: string; urlPath?: string | null } | null;
  /** Own sale start, "YYYY-MM-DD". */
  start?: string | null;
  /** Own sale end, "YYYY-MM-DD". */
  end?: string | null;
  /** Largest advertised discount, percent. */
  maxDiscount?: number | null;
}

/** The event summary from a `salePage` or `saleEvents` response. */
export interface BackendSaleEventSummary {
  id: string;
  slug: string;
  name: string;
  aliases: string[];
  /** Owner's plain-text explainer. */
  description?: string | null;
  /** Current / next edition; null once all have ended. */
  nextWindow?: BackendSaleWindow | null;
  /** Every edition, by first day (`saleEvents` rows select it). */
  windows?: readonly BackendSaleWindow[] | null;
}

/**
 * The window to display for an event's hub title and dates (plan P3): the
 * upcoming edition whenever one exists (so the title year, dates and phase
 * always point forward), otherwise the last ended edition. Tentative dates
 * keep their `expected` flag.
 *
 * `_livePromosCount` and `_now` are accepted for call-site compatibility; the
 * decision no longer depends on them (the old 6-week / live-promotion rule kept
 * last year's edition on the hub for months).
 *
 * Returns the window whose dates should be shown on the hub, or null if there
 * is nothing useful to show.
 */
export function saleEventDisplayWindow(
  nextWin: BackendSaleWindow | null | undefined,
  prevWin: BackendSaleWindow | null | undefined,
  _livePromosCount: number = 0,
  _now: Date = new Date(),
): { startDate: string; endDate: string; expected: boolean } | null {
  if (nextWin) return { startDate: nextWin.start, endDate: nextWin.end, expected: nextWin.expected };
  // All editions have ended: show the last ended window so the hub phase reads 'ended'.
  if (prevWin) return { startDate: prevWin.start, endDate: prevWin.end, expected: prevWin.expected };
  return null;
}

/** Map a backend SaleEventParticipant array to the SaleParticipant[] used by LastYearBrands. */
export function mapBackendParticipants(
  participants: readonly BackendSaleParticipant[],
): SaleParticipant[] {
  return participants.map((p) => ({
    brandName: p.brandName,
    // brand is set only when the backend confirmed public visibility; otherwise plain text.
    brandId: p.brand?.id,
    startDate: p.start ?? undefined,
    endDate: p.end ?? undefined,
    maxDiscount: p.maxDiscount ?? undefined,
  }));
}

/**
 * Map a backend `salePage` (or a `saleEvents` row) to the `SaleEvent` interface
 * used by the existing page components, applying the year-switch logic.
 *
 * The dates fall back to the event's next window, then to its last edition
 * (`windows`; once every edition has ended `nextWindow` is null and the event
 * reads as ended). Null when the backend gives no edition at all: an event
 * without dates has no title year, phase or countdown, so callers drop it
 * (a list) or answer 404 (its hub).
 *
 * Used by the SPA page loader and the SSR server (server/routes.mjs).
 */
export function mapBackendToSaleEvent(
  event: BackendSaleEventSummary,
  page: {
    nextWindow?: BackendSaleWindow | null;
    previousWindow?: BackendSaleWindow | null;
    livePromotions?: readonly unknown[];
    upcomingPromotions?: readonly unknown[];
  },
  now: Date = new Date(),
): SaleEvent | null {
  const win = saleEventDisplayWindow(
    page.nextWindow,
    page.previousWindow,
    (page.livePromotions?.length ?? 0) + (page.upcomingPromotions?.length ?? 0),
    now,
  );
  // Fall back to the event's own nextWindow if the page-level one is missing,
  // then to its last edition.
  const fallback = event.nextWindow ?? event.windows?.at(-1) ?? null;
  const startDate = win?.startDate ?? fallback?.start;
  const endDate = win?.endDate ?? fallback?.end;
  if (!startDate || !endDate) return null;
  return {
    slug: event.slug,
    name: event.name,
    aliases: event.aliases ?? [],
    startDate,
    endDate,
    expected: win?.expected ?? fallback?.expected ?? false,
    published: true,
    explainer: event.description ?? '',
    lastYearParticipants: [],
    ogImage: null,
    tagId: null,
  };
}

/**
 * A `saleEvents` list as `SaleEvent`s, in the backend's order: each row with
 * its next edition, or its last one once all have ended; a row without any
 * edition is left out (see mapBackendToSaleEvent).
 */
export function mapBackendSaleEventRows(
  rows: readonly BackendSaleEventSummary[],
  now: Date = new Date(),
): SaleEvent[] {
  return rows.flatMap((row) => mapBackendToSaleEvent(row, { nextWindow: row.nextWindow }, now) ?? []);
}
